import React, { useEffect, useRef, useState, MouseEvent } from "react";
import {
  MDXEditor,
  RealmProvider,
  type MDXEditorMethods,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";

import { useAuthContext } from "../../hooks/Auth";
import "./ContextualButton.css";
import { useOptionalChatUI, useChatUI } from "../UmmIAChatAI/ChatUIContext";
import EditorLoading from "./EditorLoading";
import { mdxPlugins } from "./config/mdxPluginsConfig";
import EditorReadOnlyImage from "./helpers/EditorReadOnlyImage";
import { useSelectionButton } from "./useSelectionButton";
import { useEditorAI } from "../../hooks/editor/useEditorAI";
import {
  ChatContext,
  MarkDownEditorElemProps,
} from "../../interfaces/Editor/Editor";
import { makePrompt } from "./helpers/makePrompt";
import EditorPopover from "./helpers/EditorPopOver";
import { useChatIntegration } from "../../hooks/editor/useChatIntegration";
import MagicButtonComp from "./helpers/MagicButton";
import { useLocation } from "react-router-dom";
import { useActividadesContext } from "../UmmIACategories/Hook/useActividades";

const MarkDownEditorElem: React.FC<MarkDownEditorElemProps> = ({
  data,
  setEditedValue,
  img,
}) => {
  const { idProyecto, resultadoId, objProyectoResultadoId } =
    useActividadesContext();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [markdown, setMarkdown] = useState<string>("");

  const mdRef = useRef<string>("");

  const editorRef = useRef<MDXEditorMethods | null>(null);

  const { loadingResponse, setLoadingResponse, lastAppliedDoc } = useChatUI();
  const location = useLocation();
  const proyecto = (location.state as any)?.proyecto;

  const editorWrapRef = useRef<HTMLDivElement | null>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [menuPinned, setMenuPinned] = useState<boolean>(false);
  const [menuQuery, setMenuQuery] = useState<string>("");
  const lastSelectionTextRef = useRef<string>("");

  const { user } = useAuthContext();
  const chat = useOptionalChatUI() as ChatContext | null;

  const setInstructionHandler = chat?.setInstructionHandler ?? (() => {});
  const setDocApplier = chat?.setDocApplier ?? (() => {});
  const setContextProvider = chat?.setContextProvider ?? (() => {});

  const { showButton, buttonPosition } = useSelectionButton(
    menuPinned,
    editorWrapRef
  );

  const proyectoIdEfectivo =
    proyecto?.proyectoId ??
    objProyectoResultadoId?.proyectoId ??
    idProyecto ??
    undefined;

  const resultadoIdEfectivo =
    proyecto?.resultadoPropuestaId ??
    objProyectoResultadoId?.resultadoId ??
    resultadoId ??
    undefined;

  const {
    isLoading: aiLoading,
    sendInstruction,
    reformulate,
    chatInstruction,
  } = useEditorAI(
    user!,
    setLoadingResponse,
    proyectoIdEfectivo,
    resultadoIdEfectivo
  );

  // ================================================================
  // Efectos iniciales
  // ================================================================
  useEffect(() => {
    console.log("Proyecto en editor:", proyecto);
  }, []);

  // Registrar proveedor de contexto y aplicador de documento para el chat
  useEffect(() => {
    setContextProvider(() => mdRef.current);

    const applier = (md: string) => {
      console.log("docApplier llamado desde ChatUIContext");
      applyNewDoc(md);
    };

    setDocApplier(applier);
    return () => setDocApplier(() => {});
  }, [setContextProvider, setDocApplier]);

  // Inicializar contenido desde `data`
  useEffect(() => {
    if (data === null || data === undefined) {
      console.log("useEffect[data] ", typeof data);
      return;
    }

    if (typeof data === "string") {
    {/**  console.log(
        "data inicial:",
        data.substring(0, 80) + "..."
      );*/} 
      setMarkdown(data);
      mdRef.current = data;
      setEditedValue(data);

      if (editorRef.current) {
        editorRef.current.setMarkdown(data);
      }

      setIsLoading(false);
    } else {
      console.warn("Data no es string:", data);
    }
  }, [data, setEditedValue]);

  useEffect(() => {
    if (markdown) {
      console.log(
        "Markdown actualizado (estado React):",
        markdown.substring(0, 50) + "..."
      );
    }
  }, [markdown]);

  // Cuando el chat aplica un doc (respuesta o "volver atrás")
  useEffect(() => {
    if (!lastAppliedDoc) return;
    if (lastAppliedDoc === mdRef.current) return;

    console.log(
      "[MarkDownEditor] lastAppliedDoc recibido:",
      lastAppliedDoc.substring(0, 80) + "..."
    );
    applyNewDoc(lastAppliedDoc);
  }, [lastAppliedDoc]);

  // ================================================================
  // Funciones principales
  // ================================================================
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    const selText = window.getSelection()?.toString() ?? "";
    lastSelectionTextRef.current = selText;
    setAnchorEl(event.currentTarget);
    setMenuPinned(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuPinned(false);
  };

  const handleEditorChange = (m: string) => {
    setMarkdown(m);
    mdRef.current = m;
    setEditedValue(m);
  };

  const applyNewDoc = (newDoc: string) => {
    console.log(
      "[MarkDownEditor] applyNewDoc() =>",
      newDoc.substring(0, 80) + "..."
    );
    setMarkdown(newDoc);
    mdRef.current = newDoc;
    setEditedValue(newDoc);

    // Forzar al MDXEditor a cargar el markdown
    if (editorRef.current) {
      try {
        editorRef.current.setMarkdown(newDoc);
      } catch (e) {
        console.error("[MarkDownEditor] Error en editorRef.setMarkdown:", e);
      }
    }
  };

  // ================================================================
  // Envío de instrucciones al backend
  // ================================================================
  const handleSubmitInstruction = async () => {
    const typed = menuQuery.trim();
    const selected =
      window.getSelection()?.toString() || lastSelectionTextRef.current.trim();
    const prompt = makePrompt(typed, selected);
    if (!prompt) return;

    const nuevoDoc = await sendInstruction(prompt, mdRef.current, "Editar");
    if (nuevoDoc) applyNewDoc(nuevoDoc.texto);
    handleClose();
  };

  // Handle highlight instruction que usa el botón contextual
  const handleHighlight = async (e: MouseEvent<HTMLDivElement>) => {
    const instruccion = (e.currentTarget as HTMLElement).innerText;
    if (!user) return;

    let selectedText = window.getSelection()?.toString()?.trim() ?? "";
    if (!selectedText) selectedText = lastSelectionTextRef.current.trim();
    if (!selectedText) return;

    try {
      const nuevoTexto = await reformulate(
        selectedText,
        mdRef.current,
        instruccion
      );

      if (nuevoTexto) {
        const escapedText = selectedText.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        );
        const regex = new RegExp(escapedText, "g");
        const newMarkdown = markdown.replace(regex, nuevoTexto);

        applyNewDoc(newMarkdown);
      }

      lastSelectionTextRef.current = "";
      window.getSelection()?.removeAllRanges();
    } catch (error) {
      console.error("Error al reformular texto:", error);
    } finally {
      handleClose();
    }
  };

  // ================================================================
  // Hook Integración con Chat AI
  // ================================================================
  useChatIntegration(
    chat,
    user,
    mdRef,
    setInstructionHandler,
    chatInstruction,
    applyNewDoc
  );

  // Loading
  if (isLoading || loadingResponse) {
    return (
      <EditorLoading
        msg="Estamos reformulando la respuesta. Esto puede tardar unos segundos."
        steps={[]}
        loopTips={[]}
      />
    );
  }

 
  // Render
 
  return (
    <div ref={editorWrapRef} style={{ position: "relative" }}>
      <MDXEditor
        ref={editorRef}
        markdown={markdown}
        onChange={handleEditorChange}
        plugins={mdxPlugins}
      />


      {/* Imagen no editable */}
      {img && <EditorReadOnlyImage img={img} />}

      {/* Botón mágico y Popover */}
      {showButton && buttonPosition && (
        <>
          <MagicButtonComp position={buttonPosition} onClick={handleClick} />
          <EditorPopover
            open={open}
            anchorEl={anchorEl}
            menuQuery={menuQuery}
            setMenuQuery={setMenuQuery}
            handleClose={handleClose}
            handleSubmitInstruction={handleSubmitInstruction}
            handleHighlight={handleHighlight}
          />
        </>
      )}
    </div>
  );
};

const MarkDownEditor: React.FC<MarkDownEditorElemProps> = (props) => (
  <RealmProvider>
    <MarkDownEditorElem {...props} />
  </RealmProvider>
);

export default MarkDownEditor;
